import { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_URL } from "../../../config";
import { AuthContext } from "../../../Context/AuthContext";
import axiosInstance from "../../../utils/axiosInstance";
import "./EmployeeDashboard.css";

const canDo = (user, module, action) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.[action] === true;
};

const canViewPage = (user, module) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.view === true;
};

function Employee() {
  const [employee, setEmployee] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getemployeeData`)
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setEmployee(res.data);
        } else {
          setEmployee([
            {
              employeeId: "EMP001",
              ename: "Aesha Gupta",
              phoneNo: "9876543210",
              official_email: "aesha@gmail.com",
              lastExp: "2 yrs",
              department: { deptName: "IT" },
              service: { serviceName: "Web Dev" },
            },
            {
              employeeId: "EMP002",
              ename: "Rahul Sharma",
              phoneNo: "9123456789",
              official_email: "rahul@gmail.com",
              lastExp: "3 yrs",
              department: { deptName: "HR" },
              service: { serviceName: "Recruitment" },
            },
          ]);
        }
      })
      .catch(() => setEmployee([]));
  }, []);

  const handleDelete = async (employeeId) => {
    if (!window.confirm("Delete this employee?")) return;
    try {
      await axiosInstance.delete(`${API_URL}/api/deleteSignUpUser/${employeeId}`);
      setEmployee(employee.filter((emp) => emp.employeeId !== employeeId));
    } catch {
      alert("Delete failed");
    }
  };

  const handleToggleBlock = async (emp) => {
    const isCurrentlyBlocked = emp?.isActive === false;
    const nextIsActive = isCurrentlyBlocked; // blocked -> true (unblock), active -> false (block)
    const actionText = isCurrentlyBlocked ? "unblock" : "block";

    if (!window.confirm(`Do you want to ${actionText} this employee?`)) return;

    try {
      const targetId = emp?.employeeId || emp?._id;
      const res = await axiosInstance.put(`${API_URL}/api/toggleEmployeeBlock/${targetId}`, {
        isActive: nextIsActive,
      });
      const updated = res?.data?.user;
      if (updated) {
        setEmployee((prev) =>
          prev.map((row) => (String(row._id) === String(updated._id) ? { ...row, ...updated } : row))
        );
      }
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to update employee status");
    }
  };

  if (!canViewPage(user, "employees")) {
    return <div className="text-center mt-5 text-danger fw-bold">Access Denied</div>;
  }

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(employee);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");
    XLSX.writeFile(wb, "Employee_List.xlsx");
  };

  return (
    <div className="empdash-page">
      <div className="empdash-shell">
        <div className="empdash-hero">
          <div>
            <p className="empdash-eyebrow">People Management</p>
            <h3>Employee List</h3>
          </div>
        </div>

        <div className="empdash-toolbar">
          {canDo(user, "employees", "add") && (
            <Link to="/admin/addemployee" className="empdash-add-btn">
              Add Employee
            </Link>
          )}

          <div className="empdash-tools-right">
            <input
              type="text"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="empdash-search"
            />
            <button onClick={exportToExcel} className="empdash-export-btn">
              Export
            </button>
          </div>
        </div>

        <div className="empdash-table-card">
          <div className="table-responsive">
            <table className="table align-middle mb-0 text-center">
              <thead>
                <tr>
                  {[
                    "Emp ID",
                    "Name",
                    "Phone",
                    "Email",
                    "Exp",
                    "Department",
                    "Service",
                    "Lock Status",
                    "Action",
                  ].map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {employee
                  .filter((emp) => emp.ename.toLowerCase().includes(search.toLowerCase()))
                  .map((emp) => (
                    <tr key={emp._id || emp.employeeId}>
                      <td>
                        <button
                          type="button"
                          className="empdash-empid-link"
                          onClick={() => navigate(`/admin/ViewEmployee/${emp.employeeId}`)}
                        >
                          {emp.employeeId}
                        </button>
                      </td>
                      <td>{emp.ename}</td>
                      <td>{emp.phoneNo}</td>
                      <td>{emp.official_email}</td>
                      <td>{emp.lastExp}</td>
                      <td>{emp.department?.deptName}</td>
                      <td>{emp.service?.serviceName}</td>
                      <td>
                        {emp.isLocked ? (
                          <div className="badge bg-danger">Locked</div>
                        ) : (
                          <div className="badge bg-success">Active</div>
                        )}
                      </td>

                      <td>
                        <div className="d-flex justify-content-center gap-2 flex-wrap">
                          <button
                            className="btn btn-sm empdash-act view"
                            onClick={() => navigate(`/admin/ViewEmployee/${emp.employeeId}`)}
                          >
                            View
                          </button>

                          <button
                            className="btn btn-sm empdash-act edit"
                            onClick={() => navigate(`/admin/upDateUder/${emp.employeeId}`)}
                          >
                            Edit
                          </button>

                          <button
                            className="btn btn-sm empdash-act delete"
                            onClick={() => handleDelete(emp.employeeId)}
                          >
                            Delete
                          </button>

                          <button
                            className={`btn btn-sm empdash-act ${emp?.isActive === false ? "unblock" : "block"}`}
                            onClick={() => handleToggleBlock(emp)}
                          >
                            {emp?.isActive === false ? "Unblock" : "Block"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Employee;
